import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import './Upload.css';
import { API_BASE, authHeaders } from '../utils/Utils.js';

function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim() !== '');
  if (lines.length === 0) return { headers: [], rows: [] };
  const headers = lines[0].split(',').map(h => h.trim());
  const rows = lines.slice(1).map(line => {
    const vals = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { inQuotes = !inQuotes; continue; }
      if (ch === ',' && !inQuotes) { vals.push(cur.trim()); cur = ''; continue; }
      cur += ch;
    }
    vals.push(cur.trim());
    return vals;
  });
  return { headers, rows };
}

function autoDetectBoolean(val) {
  const lower = val.toLowerCase().trim();
  if (['true', '1', 'yes', 'sim', 'vivo', 'viva', 'alive'].includes(lower)) return 'true';
  if (['false', '0', 'no', 'nao', 'não', 'morto', 'morta', 'dead'].includes(lower)) return 'false';
  return '';
}

function detectAndConvertDate(value) {
  if (!value) return value;
  // Normalize: replace all slashes with dashes
  const s = value.trim().replace(/\//g, '-');
  // Already YYYY-MM-DD
  if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(s)) {
    const [year, month, day] = s.split('-');
    return `${year}-${month.padStart(2,'0')}-${day.padStart(2,'0')}`;
  }
  // DD-MM-YYYY (common in Brazil)
  let m = s.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (m) {
    const [, day, month, year] = m;
    return `${year}-${month.padStart(2,'0')}-${day.padStart(2,'0')}`;
  }
  return s;
}

function StepIndicator({ current, t }) {
  const steps = [
    t('upload_step_file'),
    t('upload_step_columns'),
    t('upload_step_values'),
    t('upload_step_submit'),
  ];
  return (
    <div className="step-indicator">
      {steps.map((label, i) => (
        <div key={i} className={`step ${i === current ? 'active' : ''} ${i < current ? 'done' : ''}`}>
          <span className="step-num">{i + 1}</span>
          <span className="step-label">{label}</span>
        </div>
      ))}
    </div>
  );
}

export const Upload = (props) => {
  const { collection, displayName, dataModel, onSuccess, extraFields, step0Extra } = props;
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvRows, setCsvRows] = useState([]);
  const [columnMap, setColumnMap] = useState({});
  const [valueMap, setValueMap] = useState({});
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Check if all enum/boolean values are mapped
  const allValuesMapped = useMemo(() => {
    if (!dataModel) return true;
    return dataModel.every(field => {
      if (field.type !== 'enum' && field.type !== 'boolean') return true;
      const mapping = valueMap[field.field];
      if (!mapping) return true;
      return Object.values(mapping).every(v => v !== '');
    });
  }, [dataModel, valueMap]);

  // Check if all required fields have a column mapped
  const allRequiredFieldsMapped = useMemo(() => {
    if (!dataModel) return true;
    return dataModel.every(field => {
      if (!field.required) return true;
      return columnMap[field.field] !== undefined && columnMap[field.field] !== '';
    });
  }, [dataModel, columnMap]);

  // If no dataModel provided, fall back to old behavior
  if (!dataModel) {
    return <OldUpload collection={collection} onSuccess={onSuccess} />;
  }

  // --- Step 0: File Selection ---
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setError(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const { headers, rows } = parseCSV(ev.target.result);
      if (headers.length === 0) {
        setError(t('upload_parse_error'));
        return;
      }
      setCsvHeaders(headers);
      setCsvRows(rows);

      // Auto-match columns
      const autoMap = {};
      dataModel.forEach(field => {
        const match = headers.find(h => h.toLowerCase() === field.field.toLowerCase());
        if (match) autoMap[field.field] = match;
      });
      setColumnMap(autoMap);
      setStep(1);
    };
    reader.readAsText(file);
  };

  // --- Step 1: Column Mapping ---
  const handleColumnMapChange = (field, csvCol) => {
    setColumnMap(prev => ({ ...prev, [field]: csvCol || undefined }));
  };

  const proceedToValueMapping = () => {
    // Build initial value map for enum fields
    const initValueMap = {};
    dataModel.forEach(field => {
      if (field.type !== 'enum' && field.type !== 'boolean') return;
      const csvCol = columnMap[field.field];
      if (!csvCol) return;
      const colIdx = csvHeaders.indexOf(csvCol);
      if (colIdx === -1) return;

      // If the field has no valid target values, skip value mapping entirely.
      if (field.type === 'enum' && (!field.values || field.values.length === 0)) return;

      const uniqueVals = [...new Set(csvRows.map(r => r[colIdx]).filter(Boolean))];
      const mapping = {};
      uniqueVals.forEach(val => {
        if (field.type === 'boolean') {
          mapping[val] = autoDetectBoolean(val);
        } else {
          const matchExact = field.values.find(v => v.toLowerCase() === val.toLowerCase());
          mapping[val] = matchExact || '';
        }
      });
      initValueMap[field.field] = mapping;
    });
    setValueMap(initValueMap);
    setStep(2);
  };

  // --- Step 2: Value Mapping ---
  const handleValueMapChange = (field, rawVal, mappedVal) => {
    setValueMap(prev => ({
      ...prev,
      [field]: { ...prev[field], [rawVal]: mappedVal },
    }));
  };

  // --- Step 3: Transform & Submit ---
  const transformAndSubmit = () => {
    setSubmitting(true);
    setError(null);

    const records = csvRows.map(row => {
      const record = { ...(extraFields || {}) };
      dataModel.forEach(field => {
        const csvCol = columnMap[field.field];

        // Boolean with no column mapped: apply default
        if (!csvCol) {
          if (field.type === 'boolean' && field.default !== undefined) {
            record[field.field] = field.default;
          }
          return;
        }

        const colIdx = csvHeaders.indexOf(csvCol);
        if (colIdx === -1) {
          if (field.type === 'boolean' && field.default !== undefined) {
            record[field.field] = field.default;
          }
          return;
        }

        let val = row[colIdx] || '';

        // Apply value mapping for enums and booleans
        if ((field.type === 'enum' || field.type === 'boolean') && valueMap[field.field]?.[val]) {
          val = valueMap[field.field][val];
        }

        // Convert boolean string to JS boolean
        if (field.type === 'boolean') {
          record[field.field] = val === 'true' ? true : val === 'false' ? false : (field.default ?? true);
          return;
        }

        // Apply date conversion
        if (field.type === 'date') {
          val = detectAndConvertDate(val);
        }

        // Apply number conversion
        if (field.type === 'number') {
          const num = parseInt(val, 10);
          if (!isNaN(num)) val = num;
        }

        record[field.field] = val;
      });
      return record;
    });

    const url = `${API_BASE}/api/upload/${collection}/json`;
    fetch(url, {
      method: 'POST',
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(records),
    })
    .then(response => {
      if (response.ok) {
        onSuccess();
        return;
      }
      if (response.status === 401) {
        localStorage.removeItem('zapmanejo_token');
        localStorage.removeItem('zapmanejo_user');
        window.location.reload();
        return;
      }
      return response.text().then(t => { throw new Error(t); });
    })
    .catch(err => {
      setError(`${t('upload_failed')}: ${err.message}`);
      setSubmitting(false);
    });
  };

  return (
    <div className="UploadForm">
      <StepIndicator current={step} t={t} />

      {error && <div className="upload-error">{error}</div>}

      {/* Step 0: File Selection */}
      {step === 0 && (
        <div className="upload-step">
          <h3>{t('upload_select_file')}</h3>
          <input type="file" accept=".csv, text/csv"
            onChange={handleFileSelect} />
          {step0Extra}
        </div>
      )}

      {/* Step 1: Preview & Column Mapping */}
      {step === 1 && (
        <div className="upload-step">
          <h3>{t('upload_preview')}</h3>
          <div className="preview-table-wrapper">
            <table className="preview-table">
              <thead>
                <tr>{csvHeaders.map(h => <th key={h}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {csvRows.slice(0, 3).map((row, i) => (
                  <tr key={i}>{row.map((v, j) => <td key={j}>{v}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3>{t('upload_map_columns')}</h3>
          <p className="upload-hint">{t('upload_map_columns_hint')}</p>

          <div className="mapping-table">
            <div className="mapping-row mapping-header">
              <span>{t('upload_field')}</span>
              <span>{t('upload_csv_column')}</span>
            </div>
            {dataModel.map(field => (
              <div key={field.field} className={`mapping-row ${field.required && !columnMap[field.field] ? 'required-missing' : ''}`}>
                <span className="field-label" title={t(field.descriptionKey)}>
                  {t(field.field)}{field.required && <span className="required-marker">*</span>}
                  <small>{field.type}</small>
                </span>
                <select value={columnMap[field.field] || ''}
                  onChange={e => handleColumnMapChange(field.field, e.target.value)}>
                  <option value="">
                    {field.required
                      ? t('upload_select')
                      : field.type === 'boolean'
                        ? `${t('upload_default')}: ${String(field.default)}`
                        : t('upload_skip')}
                  </option>
                  {csvHeaders.map(h => (
                    <option key={h} value={h}>{h}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          <div className="upload-actions">
            <button onClick={() => setStep(0)}>{t('upload_back')}</button>
            <button onClick={proceedToValueMapping} disabled={!allRequiredFieldsMapped}>{t('upload_next')}</button>
          </div>
        </div>
      )}

      {/* Step 2: Value Mapping */}
      {step === 2 && (
        <div className="upload-step">
          <h3>{t('upload_map_values')}</h3>
          <p className="upload-hint">{t('upload_map_values_hint')}</p>

          {dataModel.filter(f => (f.type === 'enum' || f.type === 'boolean') && valueMap[f.field]).map(field => {
            const mapping = valueMap[field.field];
            const entries = Object.entries(mapping);
            if (entries.length === 0) return null;
            const targetOptions = field.type === 'boolean' ? ['true', 'false'] : field.values;
            return (
              <div key={field.field} className="value-mapping-group">
                <h4>{t(field.field)}</h4>
                <div className="mapping-table">
                  <div className="mapping-row mapping-header">
                    <span>{t('upload_csv_value')}</span>
                    <span>{t('upload_maps_to')}</span>
                  </div>
                  {entries.map(([rawVal, mappedVal]) => (
                    <div key={rawVal} className="mapping-row">
                      <span className="raw-value">{rawVal}</span>
                      <select value={mappedVal}
                        onChange={e => handleValueMapChange(field.field, rawVal, e.target.value)}>
                        <option value="">{t('upload_select')}</option>
                        {targetOptions.map(v => (
                          <option key={v} value={v}>{v}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {dataModel.filter(f => (f.type === 'enum' || f.type === 'boolean') && valueMap[f.field] && Object.keys(valueMap[f.field]).length > 0).length === 0 && (
            <p>{t('upload_no_enum_fields')}</p>
          )}

          <div className="upload-actions">
            <button onClick={() => setStep(1)}>{t('upload_back')}</button>
            <button onClick={() => setStep(3)} disabled={!allValuesMapped}>{t('upload_next')}</button>
          </div>
        </div>
      )}

      {/* Step 3: Review & Submit */}
      {step === 3 && (
        <div className="upload-step">
          <h3>{t('upload_review_submit')}</h3>
          <p>{t('upload_records_count', { count: csvRows.length })} <strong>{displayName || collection}</strong>.</p>

          <h4>{t('upload_column_mapping')}</h4>
          <ul className="review-list">
            {dataModel.map(f => (
              <li key={f.field}>
                <strong>{t(f.field)}</strong>:{' '}
                {columnMap[f.field]
                  ? columnMap[f.field]
                  : f.type === 'boolean'
                    ? <em>{t('upload_default')}: {String(f.default)}</em>
                    : <em>{t('upload_skipped')}</em>
                }
              </li>
            ))}
          </ul>

          <div className="upload-actions">
            <button onClick={() => setStep(2)} disabled={submitting}>{t('upload_back')}</button>
            <button onClick={transformAndSubmit} disabled={submitting}>
              {submitting ? t('upload_uploading') : t('upload_upload')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Fallback for components that don't pass dataModel
const OldUpload = ({ collection, onSuccess }) => {
  const { t } = useTranslation();
  const url = `${API_BASE}/api/upload/${collection}`;
  const submit = (formData) => {
    fetch(url, {
      method: 'POST',
      headers: authHeaders({ "enctype": "multipart/form-data" }),
      body: formData,
    })
    .then(response => {
      if (response.ok) {
        onSuccess();
        return response.json();
      }
      if (response.status === 401) {
        localStorage.removeItem('zapmanejo_token');
        localStorage.removeItem('zapmanejo_user');
        window.location.reload();
      }
      return response.text();
    })
    .catch(error => {
      console.error('Error updating form data:', error);
    });
  };

  return (
    <div className="UploadForm">
      <form action={submit}
            method="post" enctype="multipart/form-data">
        <label htmlFor="csvFile">{t('upload_choose_file')}</label>
        <input type="file" id="csvFile"
               name="csvFile" accept=".csv, text/csv" required/>
        <button type="submit" value="Upload File">{t('upload_button')}</button>
      </form>
    </div>
  );
};

export default Upload;
